import { Box, Icon, Pane, Table, TableColumnType, Text, useSorting } from '@fullstory/components';
import { ObjectDetails } from '@fullstory/proto/fs/services/dx-objects';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { allSpecObjectsBySlug, fullstorySelectors } from '@fullstory/redux';
import {
  DASHBOARD_ROUTE,
  FUNNEL_DETAILS_ROUTE,
  generateRoutePath,
  HEATMAP_DETAILS_ROUTE,
  JOURNEY_DETAILS_ROUTE,
  METRIC_DETAILS_ROUTE,
  RETENTION_DETAILS_ROUTE,
  SEGMENT_ROUTE,
  SETTINGS_ELEMENTS_DETAIL,
  SETTINGS_EVENTS_DETAIL,
  SETTINGS_PAGES_DETAIL,
  SETTINGS_SPEC_DETAIL,
} from '@fullstory/routes';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getObjectIconName,
  ObjectDetailsWithMetadata,
  toObjectDetailsWithRecords,
} from '../../../utils';
import { Pagination } from '../../Pagination';
import { usePagination } from '../../Pagination/hooks/usePagination';
import css from './index.module.scss';

interface ReferencesTableProps {
  objectDetails: ObjectDetails[];
}

export const ReferencesTable: React.FC<ReferencesTableProps> = ({ objectDetails }) => {
  const intl = useIntl();
  const [page, setPage] = React.useState(1);
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const history = useHistory();
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);

  const activeObjectDetails = useMemo(
    () =>
      objectDetails
        .map(od => toObjectDetailsWithRecords(od, specObjectsBySlug))
        .filter(o => !o.archived),
    [objectDetails, specObjectsBySlug],
  );
  const { start, end, disableNext, disablePrev, getPagedData } = usePagination({
    total: activeObjectDetails.length,
    pageSize: 10,
    page,
  });

  const handleClick = (row: ObjectDetailsWithMetadata) => {
    switch (row.objectType) {
      case 'SEGMENT':
        history.push(generateRoutePath(SEGMENT_ROUTE, { orgId, segmentId: row.id }));
        break;
      case 'METRIC':
        history.push(generateRoutePath(METRIC_DETAILS_ROUTE, { orgId, id: row.id }));
        break;
      case 'FUNNEL':
        history.push(generateRoutePath(FUNNEL_DETAILS_ROUTE, { orgId, id: row.id }));
        break;
      case 'RETENTION':
        history.push(generateRoutePath(RETENTION_DETAILS_ROUTE, { orgId, id: row.id }));
        break;
      case 'DASHBOARD':
        history.push(generateRoutePath(DASHBOARD_ROUTE, { orgId, id: row.id }));
        break;
      case 'ELEMENT':
        history.push(generateRoutePath(SETTINGS_ELEMENTS_DETAIL, { orgId, id: row.id }));
        break;
      case 'EVENT':
        history.push(generateRoutePath(SETTINGS_EVENTS_DETAIL, { orgId, id: row.id }));
        break;
      case 'PAGE':
        history.push(generateRoutePath(SETTINGS_PAGES_DETAIL, { orgId, id: row.id }));
        break;
      case 'HEATMAP':
        history.push(generateRoutePath(HEATMAP_DETAILS_ROUTE, { orgId, id: row.id }));
        break;
      case 'JOURNEY':
        history.push(generateRoutePath(JOURNEY_DETAILS_ROUTE, { orgId, id: row.id }));
        break;
      case 'SPEC_OBJECT_MAPPING':
        history.push(generateRoutePath(SETTINGS_SPEC_DETAIL, { orgId, id: row.id }));
        break;
      default:
        break;
    }
  };

  const sortingProps = useSorting(activeObjectDetails, 'last-modified', {
    defaultSortDirection: 'desc',
    sortFunction: (activeObjectDetails, key, direction) => {
      switch (key) {
        case 'last-modified':
          return orderBy(activeObjectDetails, ({ lastUpdated }) => lastUpdated, direction);
        case '30-day-views':
          return orderBy(activeObjectDetails, ({ objectViewCount }) => objectViewCount, direction);
        default:
          return activeObjectDetails;
      }
    },
  });

  const columns: TableColumnType<ObjectDetailsWithMetadata>[] = [
    {
      key: 'reference',
      label: <FormattedMessage defaultMessage="Reference" id="mcUhhf" />,
      renderCell: row => {
        const iconName = getObjectIconName(row.objectType);
        return (
          <Box
            data-component="ReferencesRow"
            testId="References-row"
            display="flex"
            gap={8}
            alignItems="center"
            width="400px"
          >
            {iconName && (
              <Box flexShrink={0}>
                <Icon name={iconName} size={16} />
              </Box>
            )}
            <Text appearance="bodyMedium" noWrap>
              {row.name}
            </Text>
          </Box>
        );
      },
    },
    {
      key: '30-day-views',
      sortable: true,
      label: <FormattedMessage defaultMessage="30-day views" id="k5l2AG" />,
      renderCell: row => <Text appearance="bodyMedium">{row.objectViewCount}</Text>,
    },
    {
      key: 'last-modified',
      sortable: true,
      label: <FormattedMessage defaultMessage="Last modified" id="1Jufsz" />,
      renderCell: row => {
        const lastUpdated = Timestamp.toDate(row.lastUpdated);
        return (
          <Text appearance="bodyMedium">
            {intl.formatMessage(
              { defaultMessage: '{lastUpdated, date, medium}', id: 'JNHjt6' },
              { lastUpdated },
            )}
          </Text>
        );
      },
    },
  ];

  return (
    <Pane data-component="ReferencesTable" borderRadius={4} border="standard">
      <Box display="flex" justifyContent="flex-end" height={40}>
        <Pagination
          alignment="left"
          total={activeObjectDetails.length}
          page={page}
          onClickNext={() => setPage(prev => prev + 1)}
          onClickPrev={() => setPage(prev => prev - 1)}
          disableNext={disableNext}
          disablePrev={disablePrev}
          start={start}
          end={end}
        />
      </Box>
      <Box paddingBottom={4}>
        <Table
          dense
          columns={columns}
          borders="head-bottom"
          rowKeyAccessor="id"
          headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
          bodyProps={{
            rowProps: {
              onClick: handleClick,
              cellProps: { className: css.tableCell },
            },
          }}
          rows={getPagedData(sortingProps.rows)}
          activeSortColumnKey={sortingProps.activeSortColumnKey}
          activeSortDirection={sortingProps.activeSortDirection}
          onSortChange={sortingProps.onSortChange}
        />
      </Box>
    </Pane>
  );
};
